// PageView.tsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Editor from '@react-page/editor';
import api from './util/api';
import { useAuth } from './AuthContext';
import { GoogleLogin } from '@react-oauth/google';

import { useHighlightCurrentLinks } from './util/highlight';

// your plugins
import createSlatePlugin from '@react-page/plugins-slate';
import { createFilePlugin } from './plugins/createFilePlugin';
import { createCustomImagePlugin } from './plugins/customImagePlugin';
import { createHrPlugin } from './plugins/createHrPlugin';
import { createParallaxContainerPlugin } from './plugins/createParallaxContainerPlugin';
import { createSimpleContainerPlugin } from './plugins/simpleContainerPlugin'
import colorPlugin from './plugins/colorPlugin';
import { createCarouselPlugin } from './plugins/carouselPlugin';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * In React Page, you define an array of "cell plugins".
 * Each plugin handles a specific type of content (text, image, etc.).
 */
const slatePlugin = createSlatePlugin(def => ({
  ...def,
  plugins: {
    //  ...def.plugins,
    alignment: def.plugins.alignment,
    // code : def.plugins.code,
    emphasize: def.plugins.emphasize,
    headings: def.plugins.headings,
    link: def.plugins.link,
    paragraphs: def.plugins.paragraphs,
    // quotes : def.plugins.quotes,
    color: {
      SetColor: colorPlugin,
    },
    lists: def.plugins.lists,
  },
}));
const hrPlugin = createHrPlugin();
const filePlugin = createFilePlugin();
const customImagePlugin = createCustomImagePlugin();
const parallaxContainerPlugin = createParallaxContainerPlugin();
const simpleContainerPlugin = createSimpleContainerPlugin();
const carouselPlugin = createCarouselPlugin();


const cellPlugins = [slatePlugin, customImagePlugin, carouselPlugin, filePlugin, hrPlugin, parallaxContainerPlugin, simpleContainerPlugin];

export interface PageJSON {
  slug: string;
  title: string;
  content: any;
}

export function isValidPage(obj: any): obj is PageJSON {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.slug === 'string' &&
    typeof obj.title === 'string' &&
    'content' in obj
  );
}

export function PageView() {
  let { slug } = useParams();  // e.g. 'home-page'
  const { user, login, isAdmin } = useAuth();
  const [page, setPage] = useState<any>(null);
  const [header, setHeader] = useState<any>(null);
  const cacheKey = 'page-header';

  if (!slug || slug.indexOf('.html') >= 0) slug = 'homepage';
  useHighlightCurrentLinks();

  useEffect(() => {

    // ----------------- check cache -----------------
    const cachedRaw = localStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (isValidPage(cached)) {
          setHeader(cached);
          // Don't return here – we still want to fetch from the API to check for updates (stale-while-revalidate)
        } else {
          // invalid JSON shape – remove it
          localStorage.removeItem(cacheKey);
        }
      } catch {
        localStorage.removeItem(cacheKey);
      }
    }
    // ----------------- fetch from API -----------------
    api
      .get<PageJSON>(`/api/pages/slug/${cacheKey}/`)
      .then((res) => {
        if (isValidPage(res.data)) {
          setHeader(res.data);
          localStorage.setItem(cacheKey, JSON.stringify(res.data));
        } else {
          console.error('API returned invalid page JSON');
        }
      })
      .catch((err) => console.error(err));
  }, [cacheKey]);

  // Load the page data from Django by slug
  useEffect(() => {
    api.get(`/api/pages/slug/${slug}/`).then((res) => {
      setPage(res.data);
    });
  }, [slug]);

  useEffect(() => {
    if (page?.title) {
      document.title = page.title;
    }
  }, [page?.title]);

  if (!slug) return <div>No slug provided</div>;
  if (!page) return <div>Loading …</div>;

  return (
    <div id={slug}>
      {/* Header always visible */}
      <header id="page-header">
        <Editor value={header?.content} cellPlugins={cellPlugins} readOnly />
        {!user && (
          <div className="login-under-facebook">
            <GoogleLogin
              onSuccess={(credentialResponse) => {
                if (credentialResponse.credential) {
                  login(credentialResponse.credential);
                }
              }}
              onError={() => console.log('Login Failed')}
            />
          </div>
        )}
      </header>

      {/* Main content */}
      <main id="page-content">
        <Editor value={page?.content} cellPlugins={cellPlugins} readOnly />
      </main>

      {isAdmin && (
        <div className="admin-actions">
          <Link to={`/editor/${slug}`} className="edit-btn">Edit Page</Link>
        </div>
      )}
    </div>

  );
}
